import React, {
  useLayoutEffect,
  useState,
  useEffect,
  useCallback
} from 'react';
import { Auth, Hub } from 'aws-amplify';
import TagManager from 'react-gtm-module';
import { Grommet, Grid, Box, Text } from 'grommet';
import {
  Router,
  RouteComponentProps,
  Redirect,
  useLocation
} from '@reach/router';
import styled from 'styled-components';
import Default, { customBase } from './themes/default.theme';
import { SAHeader, FooterComponent, UserNav, OctoBanner, GpaCancelPolicy } from './components';
import {
  AccountInformationPage,
  PolicyInformationPage,
  BillingPage,
  DocumentsInfoPage,
  Search,
  LookupToolsPage,
  CommunicationsPage,
  ClaimsPage,
  QuotePage,
  QuotesPage,
  QuotesInformationPage
} from './pages';
import SearchPageBackground from './assets/images/search-background.jpg';
import FeedbackCommentModal from './components/feedbackCommentModal';
import getConfig, { Env } from './config';
import FeatureFlag from './components/featureFlags';
import DatePickerTest from './components/datePicker/testing/DatePickerTest';
import GlobalContextProvider from './util/globalContextProvider';
import { CPRAFooter } from './util/CPRAFooter/CpraFooter';
import { devClient } from './queryClient';
import { ApolloProvider } from 'react-apollo';

declare global {
  interface Window {
    snowplow: Function;
    utag: any;
  }
}

const config = getConfig(process.env.REACT_APP_ENV as Env);
const version = `SA360-${process.env.REACT_APP_ENV}@${process.env.REACT_APP_VERSION}`;
const redirectSignInURL: URL = new URL(config.oauth.redirectSignIn);
const SearchPage = (_: RouteComponentProps) => <Search />;

// https://stackoverflow.com/questions/53058110/stop-reach-router-scrolling-down-the-page-after-navigating-to-new-page
const ScrollToTop = ({
  children
}: RouteComponentProps<{ children: React.ReactNode | React.ReactNode[] }>) => {
  const location = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return <>{children}</>;
};

const xlargeBreakpoint = `${customBase?.global?.breakpoints?.xlarge?.value}px`;

const OctoBannerContainer = styled.div`
  position: relative;
`;

const StyledContentWrapper = styled.div`
  padding: 0 3.75rem 3.75rem;
  margin: 2.5rem auto 0;
  max-width: 1320px;
  @media (min-width: ${xlargeBreakpoint}) {
    width: 90%;
  }
`;

const StyledInnerContentWrapper = styled.div`
  padding-top: 2.125rem;
  min-height: 80vh;
`;
interface AppProps {
  pathLocation: {
    pathname: string;
  };
  skipSignIn?: boolean;
}

interface GPAProps {
  gpaEnv: string;
  gpaSourceSystem: string;
  gpaAccountNumber: string;
  gpaPolicyNumber: string;
}

interface QuotesInfoProps {
  quoteId: string;
  sourceSystem: string;
}

const gtmId = 'GTM-M27F45Z';

const usePriorityScript = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = process.env.REACT_APP_ENV === 'PROD' 
      ? 'https://p11.techlab-cdn.com/67cf97955f11907d7fbb28cd.js' 
      : 'https://p11.techlab-cdn.com/67cf99e822f51d596bc0910b.js';
    script.async = false;
    script.defer = false;
    document.head.insertBefore(
      script, 
      document.head.querySelector('script') || document.head.firstChild
    );
    script.onerror = () => console.error('Monitoring script failed to load');
    return () => {
      document.head.removeChild(script);
    };
  }, []);
};

// TODO: Need unit tests for this component which test route resolution
const App = ({ skipSignIn = false, pathLocation }: AppProps) => {
  const [signedIn, setSignedIn] = useState(false);
  const [isInvalidUser, setIsInvalidUser] = useState(false);
  const [signInErrorOccurred, setSignInErrorOccurred] = useState(false);

  const [email, setEmail] = useState<string>();
  const [username, setUsername] = useState<string>();
  const [userType, setUserType] = useState<string>();

  const [isGPAEnabled, setIsGPAEnabled] = useState<boolean>(false);
  const [gpaData, setGPAData] = useState<GPAProps>({
    gpaEnv: '',
    gpaSourceSystem: '',
    gpaAccountNumber: '',
    gpaPolicyNumber:''
  })
  const [showQuotesPage, setShowQuotesPage] = useState<boolean>(false);
  const [quotesData, setQuotesData] = useState<QuotesInfoProps>({quoteId: '', sourceSystem: ''});
  const [lowerEnv, setLowerEnv] = useState<string>("l3-12");
  usePriorityScript();

  const sentryOrigin =
    process.env.REACT_APP_ENV === 'DEV'
      ? 'localhost'
      : 'agency360.msd001.stateauto.com';

  const doSignIn = useCallback(() => {
    Auth.currentAuthenticatedUser()
      .then(user => {
        setSignedIn(true);
        setEmail(user.attributes.email);
        setUsername(user.username.split('_').pop());
        setUserType(user.attributes['custom:userType']);
        sessionStorage.setItem('modalPopUpOnceIncrement', '1');
        sessionStorage.setItem(
          'EIDuserName',
          user?.signInUserSession?.idToken?.payload?.identities[0]?.userId?.toLowerCase() ??
            ''
        );
        if (localStorage.getItem('sessionIncrement')) {
          let sessionIncrement = localStorage.getItem('sessionIncrement');
          let sessionNumber;
          if (sessionIncrement) {
            sessionNumber = parseInt(sessionIncrement, 10) + 1;
            sessionIncrement = sessionNumber.toString();
            localStorage.setItem('sessionIncrement', sessionIncrement);
          }
        } else {
          localStorage.setItem('sessionIncrement', '1');
        }

        if (config.environment === 'dev') window.snowplow = () => {};
        TagManager.initialize({
          gtmId,
          dataLayer: {
            csrId: user.username
          }
        });

        CPRAFooter.initialize();
        
      })
      .catch(err => {
        if (
          err === 'not authenticated' ||
          err === 'The user is not authenticated'
        ) {
          if (window.location.search.indexOf('?code=') === -1) {
            sessionStorage.setItem('redirectLocation', window.location.href);
            Auth.federatedSignIn({
              customProvider: 'Auth0'
            }).catch(_err => {
              console.error('error signing in', _err);
            });
          }
        }
      });
  }, []);

  const doRedirectToRequestedPage = useCallback(() => {
    const redirectLocation = sessionStorage.getItem('redirectLocation');
    if (userType === 'I') {
      const redirectPath = redirectLocation
        ? new URL(redirectLocation).pathname
        : window.location.pathname;
      sessionStorage.removeItem('redirectLocation');
      window.location.assign(`${config.customer_360_url}${redirectPath}`);
    }
    sessionStorage.removeItem('redirectLocation');
    if (redirectLocation && redirectLocation !== window.location.href) {
      window.location.assign(redirectLocation);
    }
  }, [userType]);

  useEffect(() => {
    if (skipSignIn) {
      setSignedIn(true);
      return;
    }
    Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signOut':
          setSignedIn(false);
          break;
        case 'signIn_failure':
          {
            const { message } = payload.data;
            if (message === 'invalid_grant') {
              setIsInvalidUser(true);
            } else if (message === 'invalid_request') {
              doRedirectToRequestedPage();
              // setSignInErrorOccurred(true);
            }
          }
          break;
        default:
        // No action
      }
    });
  }, [skipSignIn, userType]);

  useEffect(() => {
    const { pathname } = window.location;
    const segments = pathname.split('/').filter(segment => segment !== '');
    if (skipSignIn) {
      return;
    }
    // Detect if the user is landing due to having just authenticated.
    if (signedIn) {
      if (segments && segments.length > 0 && segments[0] === "quotes") {
        setQuotesData({sourceSystem: segments[1], quoteId: segments[2]})
        setShowQuotesPage(true);
        return;
      }
      if (segments && segments.length > 1 && segments[0] !== 'account') {
        if ( segments[0]?.includes('l1') || segments[0]?.includes('l3') || segments[0]?.includes('l4')) {
          setIsGPAEnabled(true);
          setLowerEnv(segments[0]);
          setGPAData({gpaEnv: segments[0], gpaSourceSystem: segments[1], gpaAccountNumber: segments[2], gpaPolicyNumber: segments[3]})
          return;
        }
      }
      doRedirectToRequestedPage();
    } else if (window.location.host !== redirectSignInURL.host) {
      window.location.assign(
        `${config.oauth.redirectSignIn}${window.location.pathname.slice(1)}`
      );
    } else {
      doSignIn();
    }
  }, [signedIn, skipSignIn, userType]);

  let authenticationStatus = null;
  if (signInErrorOccurred) {
    authenticationStatus = (
      <h3 data-test-id="unexpectedError">
        <Text color="white">An unexpected error occurred.</Text>
      </h3>
    );
  } else if (isInvalidUser) {
    authenticationStatus = (
      <h3 data-test-id="invalidUser">
        <Text color="white">Invalid user.</Text>
      </h3>
    );
  } else if (!signedIn) {
    authenticationStatus = (
      <h3 data-test-id="notSignedInMessage">
        <Text color="white">Please wait. Signing in.</Text>
      </h3>
    );
  }
  return (
    <GlobalContextProvider>
      <Grommet plain theme={Default}>
        <FeatureFlag flagName="DatePickerTest">
          <DatePickerTest />
        </FeatureFlag>
        <Grid
          fill
          rows={['auto', 'flex', 'auto']}
          columns={['flex']}
          areas={[['header'], ['main'], ['footer']]}
        >
          <Box gridArea="header" background="background">
            <SAHeader
              hasSearch={
                pathLocation.pathname !== '/' &&
                pathLocation.pathname !== '/tools/lookup'
              }
            />
          </Box>
          <Box
            gridArea="main"
            background={{
              image:
                pathLocation.pathname === '/'
                  ? `linear-gradient(rgba(0, 32, 56, 0.76), rgba(0, 32, 56, 0)), url(${SearchPageBackground})`
                  : 'background',
              repeat: 'no-repeat',
              position: 'top',
              color: '#f4f6fa'
            }}
          >
            <FeatureFlag flagName="OCTOBanner">
              <OctoBannerContainer>
                <Router basepath="/account/:accountNumber">
                  <OctoBanner path="/overview" />
                </Router>
              </OctoBannerContainer>
            </FeatureFlag>
            <StyledContentWrapper>
              <FeedbackCommentModal userEmail={email} userName={username} />
              {authenticationStatus !== null ? (
                authenticationStatus
              ) : (
                <>
                  <Router basepath="/account/:accountNumber/">
                    {/* // We're doing this because of https://github.com/reach/router/issues/267 */}
                    <UserNav path="overview" />
                    <UserNav path="billing" />
                    <UserNav path="policy/:policyNumber" />
                    <UserNav path="documents" />
                    <UserNav path="claims" />
                    <UserNav path="communications" />
                    <UserNav path="quotes/" />
                    <UserNav path="quotes/:lob" />
                  </Router>
                  <Router>
                    <SearchPage path="/" />
                    <LookupToolsPage path="/tools/lookup" />
                    <QuotePage path="/quote/:quoteNumber" />
                  </Router>
                  <StyledInnerContentWrapper>
                    <Router basepath="/account/:accountNumber">
                      <ScrollToTop path="/">
                        <Redirect noThrow from="/" to="./overview" />
                        <AccountInformationPage path="/overview" />
                        <PolicyInformationPage path="/policy/:policyNumber" />
                        <BillingPage path="/billing" />
                        <DocumentsInfoPage path="/documents" />
                        <ClaimsPage path="/claims" />
                        <CommunicationsPage path="/communications" />
                        <QuotesPage path="/quotes/" />
                        <QuotesPage path="/quotes/:lob" />
                      </ScrollToTop>
                    </Router>
                  </StyledInnerContentWrapper>
                </>
              )}
            </StyledContentWrapper>
          </Box>
          {showQuotesPage && (
            <Box 
              gridArea='main'
              background={{
                repeat: 'no-repeat',
                position: 'top',
                color: '#f4f6fa'
              }}
            >
               <div
                  style={{
                    width: '90%',
                    padding: '0 3.75rem 3.75rem',
                    margin: '2.5rem auto 0',
                    maxWidth: '1320px'
                  }}
                >
              <QuotesInformationPage 
                quoteId={quotesData?.quoteId}
                continueQuote={false}
                sourceSystem={quotesData?.sourceSystem}
              />
              </div>
            </Box>
          )}
          {isGPAEnabled && (
              <Box
                gridArea="main"
                background={{
                  repeat: 'no-repeat',
                  position: 'top',
                  color: '#f4f6fa'
                }}
              >
                <div
                  style={{
                    width: '90%',
                    padding: '0 3.75rem 3.75rem',
                    margin: '2.5rem auto 0',
                    maxWidth: '1320px'
                  }}
                >
                  {lowerEnv.includes('l1') || lowerEnv.includes('l3') ? (
                    <ApolloProvider client={devClient}>
                      <GpaCancelPolicy
                        environment={gpaData.gpaEnv}
                        sourceSystem={gpaData.gpaSourceSystem}
                        accountNumber={gpaData.gpaAccountNumber}
                        policyNumber={gpaData.gpaPolicyNumber}
                      />
                    </ApolloProvider>
                    ) : (
                    <GpaCancelPolicy
                      environment={gpaData.gpaEnv}
                      sourceSystem={gpaData.gpaSourceSystem}
                      accountNumber={gpaData.gpaAccountNumber}
                      policyNumber={gpaData.gpaPolicyNumber}
                    />
                  )}
                </div>
              </Box>
          )}
          <Box gridArea="footer" background="background">
            <FooterComponent />
          </Box>
        </Grid>
      </Grommet>
    </GlobalContextProvider>
  );
};

export default App;
